/**
 * @file DateTimePicker.tsx
 * @brief Date + time picker: pill trigger + the date popover with time.
 *
 * @details
 * Sibling of `<DatePicker>`. Opens `DatePopover` with `withTime` right under
 * the pill (DES 01-10-2026, on Schedule for later: "we need to use new
 * picker, just add time to it"): presets, one month with month and year
 * dropdowns, then Hour and Minute pills, and Clear / Apply. It replaced the
 * right-rail Sheet. Value is an ISO string.
 *
 * @author DES
 * @date 26-07-2026
 * @updated 01-10-2026 — the date popover with time, not a side sheet
 */

import { useState } from "react";
import { format, parseISO } from "date-fns";
import { Calendar as CalendarIcon, X } from "lucide-react";
import { DatePopover } from "@/components/ui/DateRangePopover";
import { cn } from "@/shared/theme/cn";

function toDate(value: string | Date | null | undefined): Date | null {
  if (!value) return null;
  if (value instanceof Date) return Number.isNaN(value.getTime()) ? null : value;
  const d = parseISO(value);
  return Number.isNaN(d.getTime()) ? null : d;
}

export interface DateTimePickerProps {
  value?: string | null;
  onChange: (next: string | undefined) => void;
  placeholder?: string;
  size?: "sm" | "md";
  disabled?: boolean;
  className?: string;
  /** Names the popover for screen readers (e.g. "Boardroom start"). */
  title?: string;
  /** @deprecated The popover has no description line; ignored. */
  description?: string;
  /** No past days, hours or minutes: bookings (Schedule for later). */
  disablePast?: boolean;
}

export function DateTimePicker({
  value,
  onChange,
  placeholder = "Pick date & time",
  size = "md",
  disabled,
  className,
  title,
  disablePast = false,
}: DateTimePickerProps) {
  const [open, setOpen] = useState(false);

  const triggerHeight = size === "sm" ? "h-9" : "h-10";
  const display = toDate(value);
  const triggerLabel = display ? format(display, "d MMM yyyy · HH:mm") : null;

  return (
    <DatePopover
      open={open}
      onOpenChange={setOpen}
      value={display}
      onApply={(d) => onChange(d.toISOString())}
      onClear={() => onChange(undefined)}
      title={title}
      withTime
      disablePast={disablePast}
      anchorClassName="flex w-full"
      anchor={
        <button
          type="button"
          disabled={disabled}
          aria-haspopup="dialog"
          aria-expanded={open}
          onClick={() => !disabled && setOpen((o) => !o)}
          className={cn(
            "group flex w-full items-center justify-between gap-2 rounded-full border border-input bg-field px-5 text-sm transition-colors",
            triggerHeight,
            "focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2",
            "disabled:cursor-not-allowed disabled:opacity-50",
            !triggerLabel && "text-muted-foreground",
            className,
          )}
        >
          <span className="flex min-w-0 items-center gap-2">
            <CalendarIcon className="h-4 w-4 shrink-0 opacity-60" />
            <span className="truncate">{triggerLabel ?? placeholder}</span>
          </span>
          {triggerLabel ? (
            <span
              role="button"
              aria-label="Clear date"
              tabIndex={0}
              onClick={(e) => {
                e.stopPropagation();
                onChange(undefined);
              }}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.stopPropagation();
                  e.preventDefault();
                  onChange(undefined);
                }
              }}
              className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full text-muted-foreground transition-colors hover:bg-secondary hover:text-foreground"
            >
              <X className="h-3.5 w-3.5" />
            </span>
          ) : null}
        </button>
      }
    />
  );
}
